import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme, makeStyles, Chip, Typography } from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'

import { styles } from './customFilter.styles'
import { DataType, FormDialogMode, Input, SelectOption } from '@globaleris/ui.library'
import { FormDialogDefinitionProps, useFormDialog } from '../../../hooks/useFormDialog'

import moment from 'moment'
import clsx from 'clsx'
import { isEmpty } from '../../../helpers'

export interface CustomFilterProps {
  onApplyFilter: (appliedFilter: any) => void
  onPrevalidateFilter?: (filterToApply: any) => Promise<boolean>
  additionalFilters?: Input[]
  appliedFilter?: any
  enabled?: boolean
  subtitle?: string
  dateTypeSelectOptions?: SelectOption[]
  customInvalidFilterError?: string
}

const CustomFilter: React.FC<CustomFilterProps> = ({
  onApplyFilter,
  onPrevalidateFilter,
  additionalFilters,
  appliedFilter = {},
  enabled = true,
  subtitle = undefined,
  dateTypeSelectOptions = null,
  customInvalidFilterError
}) => {
  const [t] = useTranslation(['translation', 'enums'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const [startDate, setStartDate] = React.useState<Date | undefined>(appliedFilter.startDate)
  const [endDate, setEndDate] = React.useState<Date | undefined>(appliedFilter.endDate)
  const [dateType, setDateType] = React.useState<number>(
    appliedFilter.dateFilterType ??
      (dateTypeSelectOptions && dateTypeSelectOptions.length > 0
        ? dateTypeSelectOptions[0].value
        : undefined)
  )

  React.useEffect(() => {
    setStartDate(appliedFilter && appliedFilter.startDate)
    setEndDate(appliedFilter && appliedFilter.endDate)
    setDateType(
      (appliedFilter && appliedFilter.dateFilterType) ??
        (dateTypeSelectOptions && dateTypeSelectOptions.length > 0
          ? dateTypeSelectOptions[0].value
          : undefined)
    )
  }, [appliedFilter])

  interface formDependenciesProps {
    formState: any
  }

  const [formDependencies, setFormDependencies] = React.useState<formDependenciesProps>({
    formState: {
      dateFilterType:
        appliedFilter.dateFilterType ??
        (dateTypeSelectOptions && dateTypeSelectOptions.length > 0
          ? dateTypeSelectOptions[0].value
          : undefined)
    }
  })

  const invalidFilterError = (
    <div>
      <Typography variant="subtitle1" color="error">
        <ErrorOutlineIcon
          style={{
            marginRight: '2px',
            marginLeft: '-2px',
            marginTop: '-2px'
          }}
        />
        {customInvalidFilterError
          ? customInvalidFilterError
          : t('translation:components.searchFilter.invalidFilterError')}
      </Typography>
    </div>
  )

  const prevalidateSearch = async (currentFilter: any): Promise<void> => {
    if (!onPrevalidateFilter) return
    if (!currentFilter.startDate || !currentFilter.endDate) return
    setFormWaiting(true)
    setFormMode(FormDialogMode.VIEW)
    const filterIsValid = await onPrevalidateFilter(currentFilter)
    console.log(currentFilter)
    console.log(filterIsValid)
    if (filterIsValid) {
      setFormMode(FormDialogMode.EDIT)
      setFormSubtitle('')
    } else {
      setFormMode(FormDialogMode.VIEW)
      setFormSubtitle(invalidFilterError)
    }
    setFormWaiting(false)
    return
  }

  const handleInitDateChange = async (newStartDate: any) => {
    if (!moment(newStartDate).isValid()) {
      return
    }
    const formData = {
      ...formDependencies.formState,
      startDate: newStartDate
    }
    await prevalidateSearch(formData)
    if (formData.endDate !== undefined && moment(formData.endDate).isBefore(moment(newStartDate))) {
      setFormDependencies({
        ...formDependencies,
        formState: {
          ...formData,
          startDate: moment(newStartDate).toDate(),
          endDate: moment(newStartDate).toDate()
        }
      })
    } else {
      setFormDependencies({
        ...formDependencies,
        formState: {
          ...formData,
          startDate: moment(formData.startDate).toDate(),
          endDate: formData.endDate !== undefined ? moment(formData.endDate).toDate() : undefined
        }
      })
    }
  }

  const handleEndDateChange = async (newEndDate: any) => {
    if (
      !moment(newEndDate).isValid() ||
      (formDependencies.formState && formDependencies.formState.endDate === null)
    ) {
      return
    }
    const formData = {
      ...formDependencies.formState,
      endDate: newEndDate
    }
    await prevalidateSearch(formData)
    if (
      formData.startDate !== undefined &&
      moment(newEndDate).isBefore(moment(formData.startDate))
    ) {
      setFormDependencies({
        ...formDependencies,
        formState: {
          ...formData,
          startDate: moment(formData.startDate).toDate(),
          endDate: moment(formData.endDate).toDate()
        }
      })
    } else {
      setFormDependencies({
        ...formDependencies,
        formState: {
          ...formData,
          endDate: moment(formData.endDate).toDate()
        }
      })
    }
  }

  const handleFilterInputChange = (
    newValueForInput: any,
    formData: any,
    onChange?: (value: string | number, formState: object) => void
  ) => {
    // if the input has an onChange handler, we call first this handler
    onChange && onChange(newValueForInput, formData)
    //
    prevalidateSearch(formData)
  }

  const searchFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      const dateInputs: Input[] = [
        {
          name: 'startDate',
          label: t('translation:components.searchFilter.inputs.startDate'),
          type: DataType.DATE,
          required: true,
          onBlur: handleInitDateChange
        },
        {
          name: 'endDate',
          label: t('translation:components.searchFilter.inputs.endDate'),
          type: DataType.DATE,
          required: true,
          minDate: formDependencies.formState?.startDate
            ? moment(formDependencies.formState.startDate).toISOString()
            : undefined,
          onBlur: handleEndDateChange
        }
      ]

      if (dateTypeSelectOptions) {
        const dateType: Input = {
          name: 'dateFilterType',
          label: 'Filtrar fechas por:',
          type: DataType.SELECT,
          selectOptions: dateTypeSelectOptions,
          onChange(value, formState) {
            setFormDependencies(prevState => ({
              ...prevState,
              formState: {
                ...prevState.formState,
                dateFilterType: +value
              }
            }))
            prevalidateSearch(formState)
          }
        }

        dateInputs.push(dateType)
      }

      if (additionalFilters) {
        return {
          id: 'CUSTOM_FILTER',
          sections: [
            {
              inputs: dateInputs
            },
            {
              title: t('translation:components.searchFilter.moreFiltersHeaderLabel'),
              collapsible: true,
              inputs: additionalFilters.map(inputFilter => {
                // inject local onChange handler for every additional input
                return {
                  ...inputFilter,
                  clearable: true, // select inputs are clearable,
                  placeholder: inputFilter.placeholder,
                  onChange: (newValueForInput, formData) =>
                    handleFilterInputChange(newValueForInput, formData, inputFilter.onChange)
                }
              })
            }
          ]
        }
      } else {
        return {
          id: 'CUSTOM_FILTER',
          inputs: dateInputs
        }
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formDependencies, additionalFilters])

  const {
    openForm,
    updateFormDefinition,
    closeForm,
    setFormWaiting,
    formProps,
    CustomFormDialog,
    setFormSubtitle,
    setFormMode
  } = useFormDialog()

  React.useEffect(() => {
    updateFormDefinition(searchFormDefinition, formDependencies.formState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFormDefinition])

  const handleFormClose = formState => {
    closeForm()
    if (!formState) return
    onApplyFilter(formState)
  }

  const handleFormShow = async () => {
    openForm({
      definition: searchFormDefinition,
      title: t('translation:components.searchFilter.title'),
      subtitle,
      mode:
        // if filter is applied or don't have prevalidation, the form opens in edit mode.
        isEmpty(appliedFilter) || !onPrevalidateFilter ? FormDialogMode.EDIT : FormDialogMode.VIEW,
      isWaiting: false,
      onClose: handleFormClose,
      okBtnLabel: t('translation:components.searchFilter.actions.ok'),
      cancelBtnLabel: t('translation:components.searchFilter.actions.cancel'),
      closeBtnLabel: t('translation:components.searchFilter.actions.cancel')
    })
    setFormDependencies({
      ...formDependencies,
      formState: {
        ...appliedFilter,
        dateFilterType: dateType,
        startDate: startDate,
        endDate: endDate
      }
    })
  }

  return (
    <>
      <div className={classes.filters}>
        <Chip
          size="small"
          variant={enabled ? 'default' : 'outlined'}
          icon={<SearchIcon></SearchIcon>}
          clickable={enabled}
          className={clsx(classes.filter, { [classes.filterDisabled]: !enabled })}
          label={`${
            isEmpty(appliedFilter)
              ? t('translation:components.searchFilter.chipLabel')
              : `${moment(startDate).format('DD/MM/YYYY')} - ${moment(endDate).format(
                  'DD/MM/YYYY'
                )}`
          }`}
          color={isEmpty(appliedFilter) ? 'default' : 'primary'}
          onClick={() => enabled && handleFormShow()}
        />
      </div>
      <CustomFormDialog {...formProps}></CustomFormDialog>
    </>
  )
}

export default React.memo(CustomFilter)
